import React, { useState } from "react";
import { Download, ExternalLink, MessageSquare } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { formatBytes } from "../../lib/portalFiles";
import { DECISIONS, whenLine } from "../../lib/portalKinds";
import { Chip, fieldCls, secondaryBtn } from "./PortalShell";

/** `base` is "/portal" inside the portal, "/admin/portal" from the dashboard. */
export async function downloadFile(id, onError, base = "/portal") {
  try {
    window.location.assign((await api.get(`${base}/files/${id}/download`)).data.url);
  } catch (err) {
    onError?.(apiErrorMessage(err, "Nu am putut descărca fișierul."));
  }
}

function Files({ files, onError, base }) {
  return files.map((f) => (
    <li key={f.id}>
      <button type="button" onClick={() => downloadFile(f.id, onError, base)}
        className="inline-flex items-center gap-1.5 text-left text-sm text-[#373A45] hover:text-[#965EA7]">
        <Download size={14} className="shrink-0" /> {f.name} <span className="text-xs text-[#6B7280]">{formatBytes(f.size)}</span>
      </button>
    </li>
  ));
}

export function LinkList({ links }) {
  return links.map((ln, i) => (
    <li key={`${ln.url}-${i}`}>
      <a href={ln.url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 break-all text-sm text-[#373A45] hover:text-[#965EA7]">
        <ExternalLink size={14} className="shrink-0" /> {ln.label || ln.url}
        <span className="text-xs text-[#6B7280]">{ln.domain || hostOf(ln.url)}</span>
      </a>
    </li>
  ));
}

export function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; }
}

/**
 * Every version handed in, newest first, each with the feedback written on it.
 * `title` names the list for whoever reads it: "Ce ai predat" for the
 * participant, "Ce a predat" for the trainer, who may also edit their feedback.
 */
export default function SubmissionHistory({
  versions, feedback = [], title, canEditFeedback = false, onChanged, downloadBase = "/portal",
}) {
  const [error, setError] = useState("");
  if (!versions?.length) return null;
  const byVersion = feedback.reduce((acc, f) => ({ ...acc, [f.version_n]: [...(acc[f.version_n] || []), f] }), {});
  return (
    <div className="mt-8">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">{title}</h3>
      {error && <p className="mt-2 text-sm text-[#c0483f]" role="alert">{error}</p>}
      <ol className="mt-3 grid gap-3">
        {[...versions].reverse().map((v) => (
          <li key={v.n} className="rounded-2xl border border-[#E5E7EB] bg-white p-4" data-testid="portal-version">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-[#373A45]">Versiunea {v.n}</span>
              <span className="text-xs text-[#6B7280]">{whenLine(v.at)}</span>
              {v.late && <Chip tone="late">După termen</Chip>}
            </div>
            <ul className="mt-2 grid gap-1">
              <Files files={v.files} onError={setError} base={downloadBase} />
              <LinkList links={v.links} />
            </ul>
            {v.note && <p className="mt-2 whitespace-pre-line text-sm text-[#6B7280]">{v.note}</p>}
            {(byVersion[v.n] || []).map((f) => (
              <FeedbackItem key={f.id} item={f} canEdit={canEditFeedback} onChanged={onChanged} onError={setError}
                base={downloadBase} />
            ))}
          </li>
        ))}
      </ol>
    </div>
  );
}

function FeedbackItem({ item, canEdit, onChanged, onError, base }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [busy, setBusy] = useState(false);
  const decision = DECISIONS[item.decision];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/portal/feedback/${item.id}`, { text });
      setEditing(false);
      onChanged?.();
    } catch (err) {
      onError(apiErrorMessage(err, "Nu am putut salva feedbackul."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-[#965EA7]/20 bg-[#965EA7]/[0.04] p-3" data-testid="portal-feedback">
      <div className="flex flex-wrap items-center gap-2 text-xs text-[#6B7280]">
        <MessageSquare size={14} className="text-[#965EA7]" />
        <span className="font-semibold text-[#373A45]">{item.author_name}</span>
        <span>{whenLine(item.created_at)}</span>
        {item.edited_at && <span>· actualizat la {whenLine(item.edited_at)}</span>}
        {item.decision !== "none" && decision && <Chip tone={decision.tone}>{decision.label}</Chip>}
      </div>
      {editing ? (
        <div className="mt-2 grid gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className={fieldCls} />
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={busy} className={secondaryBtn}>Salvează</button>
            <button type="button" onClick={() => { setEditing(false); setText(item.text); }}
              className="px-2 text-sm font-semibold text-[#6B7280]">Renunță</button>
          </div>
        </div>
      ) : (
        item.text && <p className="mt-1.5 whitespace-pre-line text-sm text-[#373A45]">{item.text}</p>
      )}
      {item.files?.length > 0 && <ul className="mt-2 grid gap-1"><Files files={item.files} onError={onError} base={base} /></ul>}
      {canEdit && !editing && (
        <button type="button" onClick={() => setEditing(true)} className="mt-2 text-xs font-semibold text-[#965EA7] hover:underline">
          Editează
        </button>
      )}
    </div>
  );
}
